"use client";

/**
 * Client-Side Logger
 *
 * Provides structured logging for browser-side code.
 * Outputs formatted console messages with timestamps and context.
 *
 * Usage:
 * ```typescript
 * import { clientLogger } from '@/lib/client-logger';
 *
 * clientLogger.info("Operation completed", { userId: "123" });
 * clientLogger.error("Operation failed", { error: err.message });
 * ```
 */

export type ClientLogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  [key: string]: unknown;
}

const LOG_LEVELS: Record<ClientLogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// In production, only show info and above; in development show everything
const currentLogLevel: ClientLogLevel =
  process.env.NODE_ENV === "production" ? "info" : "debug";

// Prefix for identifying WAX/blockchain related logs
const LOG_PREFIX = "[WAX-CLIENT]";

/**
 * Structured logger for client-side code
 */
export const clientLogger = {
  log(level: ClientLogLevel, message: string, context?: LogContext): void {
    if (LOG_LEVELS[level] < LOG_LEVELS[currentLogLevel]) {
      return;
    }

    const prefix = {
      debug: "D",
      info: "I",
      warn: "W",
      error: "E",
    }[level];

    const logMessage = `${LOG_PREFIX} [${prefix}] ${message}`;

    if (context && Object.keys(context).length > 0) {
      console[level === "debug" ? "log" : level](logMessage, context);
    } else {
      console[level === "debug" ? "log" : level](logMessage);
    }
  },

  debug(message: string, context?: LogContext): void {
    this.log("debug", message, context);
  },

  info(message: string, context?: LogContext): void {
    this.log("info", message, context);
  },

  warn(message: string, context?: LogContext): void {
    this.log("warn", message, context);
  },

  error(message: string, context?: LogContext): void {
    this.log("error", message, context);
  },
};

// Default export for convenience
export default clientLogger;
